import React from 'react';
import ConsolePanel from '../../components/ConsolePanel';
import { TerminalPanel } from '../terminal/TerminalPanel';
import { ProblemsPanel } from '../problems/ProblemsPanel';
import { NetworkPanel } from '../network/NetworkPanel';
import GitDiffPanel from '../../components/GitDiffPanel';
import { CommitPanel } from '../../components/CommitPanel';
import { LogEntry, LogLocation, LogFilter, TaskTab } from '../../types';
import { GitCommit } from '../../hooks/useGit';

type LogPanelProps = {
  filteredLogs: LogEntry[];
  logFilter: LogFilter;
  taskTab: TaskTab;
  logView: 'console' | 'terminal' | 'problems' | 'network';
  taskRunning: string | null;
  hasTaskEndpoint: boolean;
  diffLines: { type: 'add' | 'del' | 'equal'; text: string; left?: number; right?: number }[];
  activePath: string | null;
  commits: {
    id: string;
    message: string;
    author: string;
    date: string;
    summary: string;
    diff: string[];
  }[];
  selectedCommitId: string;
  // New git commits
  gitCommits?: GitCommit[];
  isLoadingGitCommits?: boolean;
  onFetchGitCommits?: (count?: number) => Promise<GitCommit[]>;
  onChangeFilter: (v: LogFilter) => void;
  onChangeTaskTab: (v: TaskTab) => void;
  onClearTaskLogs: (task: TaskTab) => void;
  onChangeLogView: (v: 'console' | 'terminal' | 'problems' | 'network') => void;
  onLint: () => void;
  onTest: () => void;
  onBuild: () => void;
  getLocationFromLog: (log: LogEntry) => LogLocation | null;
  onNavigateLocation: (loc: LogLocation) => void;
  onSelectCommit: (id: string) => void;
};

export const LogPanel = React.memo(function LogPanel({
  filteredLogs,
  logFilter,
  taskTab,
  logView,
  taskRunning,
  hasTaskEndpoint,
  diffLines,
  activePath,
  commits,
  selectedCommitId,
  gitCommits,
  isLoadingGitCommits,
  onFetchGitCommits,
  onChangeFilter,
  onChangeTaskTab,
  onClearTaskLogs,
  onChangeLogView,
  onLint,
  onTest,
  onBuild,
  getLocationFromLog,
  onNavigateLocation,
  onSelectCommit,
}: LogPanelProps) {
  return (
    <>
      <div className="console-card">
        <div className="panel-header">
          <div>
            <p className="eyebrow">Console</p>
            <h2>Tasks & output</h2>
          </div>
          <div className="log-tabs">
            {(['console', 'terminal', 'problems', 'network'] as const).map((tab) => (
              <button
                key={tab}
                className={`pill-btn ${logView === tab ? 'active' : ''}`}
                onClick={() => onChangeLogView(tab)}
                type="button"
              >
                {tab === 'console' ? 'Console' : tab === 'terminal' ? 'Terminal' : tab === 'problems' ? 'Problems' : 'Network'}
              </button>
            ))}
          </div>
        </div>
        {logView === 'console' ? (
          <ConsolePanel
            logs={filteredLogs}
            logFilter={logFilter}
            taskTab={taskTab}
            taskRunning={taskRunning}
            hasTaskEndpoint={hasTaskEndpoint}
            onLint={onLint}
            onTest={onTest}
            onBuild={onBuild}
            getLocationFromLog={getLocationFromLog}
            onNavigateLocation={onNavigateLocation}
            onChangeFilter={onChangeFilter}
            onChangeTaskTab={onChangeTaskTab}
            onClearTaskLogs={onClearTaskLogs}
          />
        ) : logView === 'terminal' ? (
          <TerminalPanel />
        ) : logView === 'problems' ? (
          <ProblemsPanel onNavigate={onNavigateLocation} />
        ) : (
          <NetworkPanel />
        )}
      </div>

      <GitDiffPanel filename={activePath} diff={diffLines} />

      <CommitPanel
        commits={commits}
        selectedCommitId={selectedCommitId}
        onSelect={onSelectCommit}
        gitCommits={gitCommits}
        isLoadingGitCommits={isLoadingGitCommits}
        onFetchGitCommits={onFetchGitCommits}
      />
    </>
  );
});
